// SCR-233's dialogs: the create/edit form (name, https URL, format, events, and — staff only —
// the owner), the one-time secret after a create or a rotate (copy button; the secret lives only
// in this dialog's props and goes with it), and the confirmation used by Rotate, Test,
// Enable/Disable and Delete. The URL is never shown back: an edit starts with it empty, and an
// empty URL keeps the stored one.
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { useApi } from "../../api/context";
import type { ApiError } from "../../api/errors";
import type { OrgContext } from "../../api/org-context";
import { ConfirmDialog, Dialog } from "../../primitives/dialog";
import { Checkbox, Field, Fieldset } from "../../primitives/field";
import { fieldErrorsFrom, type FieldErrors } from "../../primitives/forms";
import { Icon } from "../../primitives/icons";
import { ErrorDetails } from "../../primitives/states";
import {
  asApiError,
  blankWebhookForm,
  createWebhook,
  EVENT_TYPES,
  eventLabel,
  formatLabel,
  FORMATS,
  formFromWebhook,
  isWebhookLimit,
  LIMITS,
  markCopied,
  patchChanges,
  patchWebhook,
  toCreateRequest,
  toggleEvent,
  toPatchRequest,
  urlFieldFailure,
  validateWebhookForm,
  webhookWriteFailure,
  type NotificationWebhook,
  type NotificationWebhookWithSecret,
  type SecretReveal,
  type WebhookFormMode,
  type WebhookFormValues,
  type WebhookOwner,
} from "./model";

export interface WebhookFormProps {
  context: OrgContext;
  mode: WebhookFormMode;
  owners: readonly WebhookOwner[];
  onClose(): void;
  /** A create answers with the secret; an edit does not. */
  onCreated(answer: NotificationWebhookWithSecret): void;
  onSaved(webhook: NotificationWebhook): void;
}

export function WebhookForm({ context, mode, owners, onClose, onCreated, onSaved }: WebhookFormProps) {
  const client = useApi();
  const [values, setValues] = useState<WebhookFormValues>(() => (mode.kind === "edit" ? formFromWebhook(mode.webhook) : blankWebhookForm()));
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);

  const editing = mode.kind === "edit";
  const staff = context.surface === "staff";
  const clientErrors = validateWebhookForm(values, mode, owners);
  const urlFailure = failure ? urlFieldFailure(failure) : null;
  const errors: FieldErrors = { ...clientErrors, ...(failure ? fieldErrorsFrom(failure) : {}), ...(urlFailure ? { url: urlFailure } : {}) };
  const shown = (field: string) => (touched || failure ? errors[field] : undefined);
  const patch = mode.kind === "edit" ? toPatchRequest(values, mode.webhook) : null;
  const unchanged = patch !== null && !patchChanges(patch);

  const set = (next: WebhookFormValues) => {
    setFailure(null);
    setValues(next);
  };
  const save = () => {
    setTouched(true);
    if (Object.keys(clientErrors).length > 0 || saving || unchanged) return;
    setSaving(true);
    setFailure(null);
    const write =
      mode.kind === "edit" && patch
        ? patchWebhook(client, mode.webhook, patch).then(onSaved)
        : createWebhook(client, context.orgId, toCreateRequest(values)).then(onCreated);
    write.catch((error: unknown) => setFailure(asApiError(error))).finally(() => setSaving(false));
  };

  return (
    <Dialog
      title={editing ? `Edit ${mode.webhook.name}` : "New webhook"}
      open
      onClose={onClose}
      class="dialog-wide"
      closeLabel="Close the form"
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" class="button button-primary" disabled={saving || unchanged} onClick={save}>
            {saving ? "Saving…" : editing ? "Save changes" : "Create webhook"}
          </button>
        </>
      }
    >
      <form
        class="stack"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        {failure && !urlFailure && (
          <div class={isWebhookLimit(failure) ? "banner banner-warning" : "banner banner-error"} role="alert">
            <p>{webhookWriteFailure(failure)}</p>
            {!isWebhookLimit(failure) && <ErrorDetails error={failure} />}
          </div>
        )}
        <Field id="webhook-name" label="Name" help="notifications.name" error={shown("name")}>
          <input id="webhook-name" type="text" maxLength={LIMITS.name} value={values.name} onInput={(event) => set({ ...values, name: event.currentTarget.value })} />
        </Field>
        <Field
          id="webhook-url"
          label="URL"
          help="notifications.url"
          hint={editing ? `Now sending to ${mode.webhook.urlHost}. Leave empty to keep the current URL.` : "https only. Stored encrypted and never shown again."}
          error={shown("url")}
        >
          <input
            id="webhook-url"
            type="url"
            inputMode="url"
            autoComplete="off"
            spellcheck={false}
            maxLength={LIMITS.url}
            placeholder="https://"
            value={values.url}
            onInput={(event) => set({ ...values, url: event.currentTarget.value })}
          />
        </Field>
        <Field id="webhook-format" label="Format" help="notifications.format">
          <select id="webhook-format" value={values.format} onChange={(event) => set({ ...values, format: event.currentTarget.value as WebhookFormValues["format"] })}>
            {FORMATS.map((format) => (
              <option key={format} value={format}>
                {formatLabel(format)}
              </option>
            ))}
          </select>
        </Field>
        <Fieldset label="Events" help="notifications.events" error={shown("eventTypes")}>
          {EVENT_TYPES.map((event) => (
            <Checkbox
              key={event}
              id={`webhook-event-${event}`}
              label={eventLabel(event)}
              help="notifications.events"
              checked={values.eventTypes.includes(event)}
              onChange={(on) => set(toggleEvent(values, event, on))}
            />
          ))}
        </Fieldset>
        {staff && (
          <Field id="webhook-owner" label="Owner" help="notifications.owner" error={shown("owner")} hint={editing ? "A webhook's owner cannot change." : undefined}>
            <select id="webhook-owner" value={values.owner} disabled={editing} onChange={(event) => set({ ...values, owner: event.currentTarget.value as WebhookOwner })}>
              {(owners.includes("org") || values.owner === "org") && <option value="org">Organization</option>}
              {(owners.includes("platform") || values.owner === "platform") && <option value="platform">AllCode</option>}
            </select>
          </Field>
        )}
        {unchanged && <p class="field-hint">Nothing to save yet.</p>}
      </form>
    </Dialog>
  );
}

/** The signing secret, once: copy it now; closing drops it. */
export function SecretRevealDialog({ reveal, onChange, onClose }: { reveal: SecretReveal; onChange(next: SecretReveal): void; onClose(): void }) {
  const copy = () => void navigator.clipboard?.writeText(reveal.secret).then(() => onChange(markCopied(reveal)), () => undefined);
  return (
    <Dialog
      title={`Signing secret for ${reveal.webhookName}`}
      open
      onClose={onClose}
      actions={
        <button type="button" class="button button-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <div class="stack-sm">
        <p>Copy this secret now and store it with the receiver. It is shown only this once; if it is lost, rotate it.</p>
        <code class="secret-value webhook-secret">{reveal.secret}</code>
        <div class="row">
          <button type="button" class="button button-sm" onClick={copy}>
            <Icon name={reveal.copied ? "check" : "copy"} small />
            {reveal.copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p class="field-hint">Each delivery carries X-Nexus-Signature, an HMAC-SHA256 of the body with this secret.</p>
      </div>
    </Dialog>
  );
}

export interface ConfirmedActionProps {
  title: string;
  message: ComponentChildren;
  confirmLabel: string;
  typedName?: string | undefined;
  /** Runs the write; resolves when done. */
  run(): Promise<unknown>;
  onDone(): void;
  onClose(): void;
}

/** A write the admin confirms first; a failure shows in the dialog. */
export function ConfirmedAction({ title, message, confirmLabel, typedName, run, onDone, onClose }: ConfirmedActionProps) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const confirm = () => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    run()
      .then(onDone)
      .catch((error: unknown) => setFailure(asApiError(error)))
      .finally(() => setBusy(false));
  };
  return (
    <ConfirmDialog
      title={title}
      open
      message={
        <>
          {message}
          {failure && <p role="alert">{webhookWriteFailure(failure)}</p>}
        </>
      }
      confirmLabel={confirmLabel}
      {...(typedName !== undefined ? { typedName } : {})}
      busy={busy}
      onConfirm={confirm}
      onClose={onClose}
    />
  );
}
